import { ChatRoom, RoomMessage } from '@/types/message';
import { apiClient } from '@/lib/api-client';

/**
 * Static fallback rooms — used only when the backend API is unreachable.
 */
const FALLBACK_ROOMS: ChatRoom[] = [
  {
    id: 'room-welcome-system',
    slug: 'welcome-room',
    name: 'Welcome Room (System)',
    description: 'Report bugs (flickering, crashing, etc.) to support@memads.in. Ask questions, meet fellow buyers & sellers, or get free ad posting tips.',
    topic: 'Welcome to MemAds! Introduce yourself • Ad posting tips • Verified sellers & buyers • Report bugs',
    onlineCount: 0,
    isSystem: true,
    verified: true,
    category: 'SYSTEM',
    icon: '💬',
    badge: 'System',
  },
  {
    id: 'room-deals-lounge',
    slug: 'deals-lounge',
    name: 'Deals & Bargains Lounge',
    description: 'Share your best classifieds finds, bargain tips, price checks, and negotiation advice with the MemAds community.',
    topic: 'Discuss trending deals on used cars, mobiles, laptops & furniture • Fair negotiation tips',
    onlineCount: 0,
    isSystem: false,
    verified: true,
    category: 'COMMUNITY',
    icon: '🏷️',
    badge: 'Trending',
  },
  {
    id: 'room-help-support',
    slug: 'help-support',
    name: 'Community Help & Support',
    description: 'Got questions about posting ads, account verification, payments, or safety? Our support team & community members are here.',
    topic: 'Platform FAQs • How to verify your account • Safe meetup guidelines • Protect yourself from scams',
    onlineCount: 0,
    isSystem: true,
    verified: true,
    category: 'SYSTEM',
    icon: '🛡️',
    badge: 'Support',
  },
];

export const chatRoomService = {
  /**
   * Fetch featured rooms from the backend API.
   * Falls back to static data if the API is unreachable.
   */
  async getFeaturedRooms(): Promise<ChatRoom[]> {
    try {
      const data = await apiClient.get('/api/messages/chat-rooms/featured');
      if (Array.isArray(data) && data.length > 0) {
        return data.map((room: any) => ({
          id: room.id || room.slug,
          slug: room.slug,
          name: room.name,
          description: room.description,
          topic: room.topic || '',
          onlineCount: room.onlineCount ?? 0,
          isSystem: room.system ?? room.isSystem ?? false,
          verified: room.verified ?? false,
          category: room.category || 'COMMUNITY',
          icon: room.icon || '💬',
          badge: room.badge,
        }));
      }
    } catch (err) {
      console.warn('[chatRoomService] Failed to fetch featured rooms from API, using fallback:', err);
    }
    return [...FALLBACK_ROOMS];
  },

  /**
   * Send a heartbeat to mark the user as online in a room.
   * Returns the updated online count.
   */
  async sendHeartbeat(slug: string): Promise<number | null> {
    try {
      const data = await apiClient.post(`/api/messages/chat-rooms/${slug}/heartbeat`, {});
      return data?.onlineCount ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Fetch room messages from the backend API.
   * Returns real persisted messages from the server.
   */
  async getRoomMessages(slug: string, limit = 50): Promise<RoomMessage[]> {
    try {
      const data = await apiClient.get(`/api/messages/chat-rooms/${slug}/messages?limit=${limit}`);
      if (Array.isArray(data)) {
        return data.map((msg: any) => ({
          id: msg.id,
          roomId: msg.roomSlug || slug,
          senderId: msg.senderId,
          senderName: msg.senderName,
          senderAvatar: msg.senderAvatar || undefined,
          text: msg.text || msg.content || '',
          createdAt: msg.createdAt,
          isBot: msg.bot ?? msg.isBot ?? false,
        }));
      }
    } catch (err) {
      console.warn('[chatRoomService] Failed to fetch room messages:', err);
    }
    return [];
  },

  /**
   * Send a message to a chat room via the backend API.
   */
  async sendRoomMessage(
    slug: string,
    sender: { id: string; name: string; avatar?: string },
    text: string
  ): Promise<RoomMessage | null> {
    try {
      const data = await apiClient.post(`/api/messages/chat-rooms/${slug}/messages`, {
        content: text.trim(),
      });
      if (data && data.id) {
        return {
          id: data.id,
          roomId: data.roomSlug || slug,
          senderId: data.senderId || sender.id,
          senderName: data.senderName || sender.name,
          senderAvatar: data.senderAvatar || sender.avatar,
          text: data.text || data.content || text.trim(),
          createdAt: data.createdAt || new Date().toISOString(),
          isBot: false,
        };
      }
    } catch (err) {
      console.error('[chatRoomService] Failed to send room message:', err);
    }
    return null;
  },

  getRoomDetails(roomId: string): ChatRoom | undefined {
    return FALLBACK_ROOMS.find(r => r.id === roomId || r.slug === roomId);
  },
};
